import { createTheme } from '@mui/material/styles';
import {
  borderWidth,
  color,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  size,
  space,
} from '@platform/tokens';

/**
 * The admin's MUI theme.
 *
 * **The mapping is deliberately shallow, and that is the decision — not an
 * unfinished job.** The brand colour, corner radius and font family come from
 * `@platform/tokens`, as do the refresh's sizes and greys; the rest is MUI's.
 *
 * The admin is not supposed to look like the mobile app. It is dense tables and
 * forms, exactly where a React Native design system is weakest, and driving MUI's
 * whole theme from one arrives more slowly somewhere MUI already was. What the three
 * shared values buy is that the brand reads as one brand. docs/specs/004-admin.md §3.
 */

/**
 * `fontFamily.sans` is `'System'`, which is React Native's name for the platform
 * font and means nothing to a browser — CSS would fall back to the default serif.
 * `system-ui` is the same intent expressed in CSS, so this is a translation of the
 * token rather than a departure from it.
 */
const WEB_FONT_STACK: Record<string, string> = {
  System: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  Menlo: 'Menlo, Consolas, "Liberation Mono", monospace',
};

/**
 * `colorSchemeSelector: 'media'` is what makes light and dark follow the OS *live*.
 * MUI emits both palettes as CSS custom properties inside a
 * `prefers-color-scheme` media query, so the switch is the browser re-evaluating a
 * media query — no React state, no re-render, and no reload.
 *
 * The obvious-looking alternative, `useMediaQuery` picking between two themes, was
 * tried first and is subtly wrong: it reads the preference at mount and does not
 * repaint an already-open page when the OS changes underneath it.
 */
const TINTED = ['info', 'success', 'warning', 'error', 'primary'] as const;

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'media' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: color.accent[500] },
        error: { main: color.danger[500] },
        warning: { main: color.warning[500] },
        success: { main: color.success[500] },
        info: { main: color.info[500] },
        background: { default: color.neutral[50] },
      },
    },
    dark: {
      palette: {
        // The lighter ramp step: accent[500] does not carry enough contrast
        // against a near-black surface.
        primary: { main: color.accent[400] },
        error: { main: color.danger[300] },
        warning: { main: color.warning[300] },
        success: { main: color.success[300] },
        info: { main: color.info[300] },
        background: {
          default: color.neutral[950],
          paper: color.neutral[900],
        },
      },
    },
  },
  shape: { borderRadius: radius.md },
  typography: {
    fontFamily: WEB_FONT_STACK[fontFamily.sans] ?? fontFamily.sans,
    // MUI's defaults are tuned for marketing pages. A back-office reads long
    // labels in narrow columns, where the tighter leading is what hurts.
    body1: { fontSize: fontSize.sm, lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    h5: { fontSize: fontSize['2xl'], fontWeight: Number(fontWeight.semibold) },
  },

  /**
   * Density and legibility defaults, not a second design system — §3.1 still holds.
   *
   * Each entry states what its call sites already say by hand: every `Table`, and
   * most `Button`s, `TextField`s and `Chip`s, pass `size="small"` today. A default is
   * that decision made once, where the next one cannot forget it. `Select` and
   * `Autocomplete` are deliberately absent — no call site asks them for `small`, and
   * defaulting them would be a size change dressed as a consolidation.
   */
  components: {
    MuiCssBaseline: {
      // `:not([class])` keeps this off MUI's own anchors — a `Button` or nav row
      // rendered as a link — which carry a class and style themselves.
      styleOverrides: (theme) => ({
        'a:not([class])': {
          color: theme.vars.palette.primary.main,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
          // `main` falls short of 4.5:1 on a dark card; links are text, not fills.
          ...theme.applyStyles('dark', {
            color: theme.vars.palette.primary.light,
          }),
        },
      }),
    },
    MuiButton: {
      // MUI shouts button labels in capitals, which tells a reader nothing in a tool
      // whose buttons read "Regenerate scene" and "Add path members".
      styleOverrides: { root: { textTransform: 'none' } },
      defaultProps: { disableElevation: true },
    },
    // The shell scrolls, not the table, so a sticky header pins to the top of the
    // screen's scroll region. Its `separate` borders are what let the table round its
    // corners; the head's corner cells round with it or they paint over the curve.
    MuiTable: {
      defaultProps: { size: 'small', stickyHeader: true },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          border: `${String(borderWidth.hairline)}px solid ${theme.vars.palette.divider}`,
          borderRadius: radius.md,
          '& thead th:first-of-type': { borderTopLeftRadius: radius.md },
          '& thead th:last-of-type': { borderTopRightRadius: radius.md },
          '& tbody tr:last-of-type td': { borderBottom: 0 },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { 'tbody &': { height: size.xl } } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontSize: fontSize.xs,
          fontWeight: Number(fontWeight.medium),
          color: theme.vars.palette.text.secondary,
          backgroundColor: theme.vars.palette.background.paper,
        }),
        sizeSmall: {
          'tbody &': { paddingTop: space.xs, paddingBottom: space.xs },
        },
        // An inline control in a row drops its outline until it is pointed at or
        // focused; the arrow stays, so the cell still reads as editable.
        body: ({ theme }) => ({
          '& .MuiOutlinedInput-root:not(:hover):not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
            { borderColor: 'transparent' },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.divider,
          },
        }),
      },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    // 36px for every small field, so a filter bar of selects, pickers and search
    // boxes lines up. MUI's small input is 40px at a 16px body; this is 14px.
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          variants: [
            {
              props: ({ ownerState }) =>
                ownerState.size === 'small' && ownerState.multiline !== true,
              style: { paddingTop: space.sm, paddingBottom: space.sm },
            },
          ],
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiInputBase-sizeSmall': {
            paddingTop: space.xs,
            paddingBottom: space.xs,
            '& .MuiAutocomplete-input': {
              paddingTop: space.xs,
              paddingBottom: space.xs,
            },
          },
        },
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      // A status reads as a soft tinted pill with darker text in its own hue, the
      // same meaning and colour as before at a fraction of the weight.
      styleOverrides: {
        root: ({ theme }) => ({
          variants: TINTED.map((tone) => ({
            props: { variant: 'filled', color: tone },
            style: {
              backgroundColor: theme.alpha(theme.vars.palette[tone].main, 0.14),
              color: theme.vars.palette[tone].dark,
              ...theme.applyStyles('dark', {
                color: theme.vars.palette[tone].light,
              }),
            },
          })),
        }),
      },
    },
  },
});
